"use client";

import {
	chakra,
	Grid,
	Link,
	useMultiStyleConfig,
	VStack,
} from "@chakra-ui/react";
import React from "react";
import { useTranslatedString } from "src/i18n/hooks";
import { truthy } from "src/lib/utils";
import { useGetProductsFromStructuredText } from "src/sections/ProductComparison/utils";
import { ProductOverviewItem } from "./ProductOverviewItem";
import { getLinkProps } from "../Link/utils";
import { NextLink } from "../NextLink";
import {
	StructuredText,
	StructuredTextData,
} from "../StructuredText/StructuredText";
import {
	TopicSelector,
	useTopicSelector,
} from "../TopicSelector/TopicSelector";

export type ProductOverviewSectionProps = {
	structuredText?: StructuredTextData;
	topicSelectorText?: string | null;
	introAlignment: string;
};

export const ProductOverviewSection: React.FC<ProductOverviewSectionProps> = ({
	structuredText,
	topicSelectorText,
	introAlignment,
}) => {
	const t = useTranslatedString();
	const styles = useMultiStyleConfig("ProductOverview", { introAlignment });

	const { intro, products } =
		useGetProductsFromStructuredText(structuredText);

	const productFamilies = products
		.map((product) => product.linkedProduct?.productFamily)
		.filter(truthy);

	const { allTopics, currentTopic, setCurrentTopic } = useTopicSelector(
		productFamilies.map(({ name, slug, color: { hex } }) => ({
			name,
			slug,
			color: hex,
		})),
	);

	const productsFiltered = products.filter((p) =>
		currentTopic === t("blogpost.allTopics.slug")
			? p
			: p.linkedProduct?.productFamily.slug === currentTopic,
	);

	const matchingProducts = products.find(
		(p) => p.linkedProduct?.productFamily.slug === currentTopic,
	);

	const familyName = matchingProducts?.linkedProduct?.productFamily.name;

	return (
		<chakra.div backgroundColor="brand.lighter" padding="2rem">
			<VStack maxWidth="container.xl" gap={12} margin="auto">
				{intro && (
					<chakra.div __css={styles.intro}>
						<StructuredText data={intro} />
					</chakra.div>
				)}
				{allTopics.length > 2 && (
					<TopicSelector
						topicSelectorText={
							topicSelectorText ?? t("blogpost.chooseTopic")
						}
						currentTopic={currentTopic}
						setCurrentTopic={setCurrentTopic}
						allTopics={allTopics}
					/>
				)}
				<Grid
					sx={styles.grid}
					templateColumns={{
						base: "repeat(1, 1fr)",
						md: "repeat(2, 1fr)",
						xl: "repeat(3, 1fr)",
					}}
				>
					{productsFiltered.map(
						({ id, title, linkedProduct: product }) => {
							if (!product) return null;

							const isProductCategoryRecord =
								product.__typename === "ProductCategoryRecord";

							if (isProductCategoryRecord) {
								return (
									<ProductOverviewItem
										key={id}
										id={id}
										name={title}
										href={`/${product.productFamily.slug}/${product.slug}`}
										image={product.img}
										description={product.teaserText ?? ""}
										price={product.price}
										priceDescription={
											product.priceDescription
										}
										isOnlineApplication={product._allReferencingProductPages.every(
											({ isOnlineApplication }) =>
												Boolean(isOnlineApplication),
										)}
									/>
								);
							}

							const externalLink = product.externalLink
								? getLinkProps(product.externalLink)
								: null;

							return (
								<ProductOverviewItem
									key={id}
									id={id}
									name={title}
									href={`/${product.productFamily.slug}/${product.slug}`}
									image={product.img}
									description={product.teaserText ?? ""}
									externalLink={externalLink}
									price={
										product.currentVersion?.price ?? null
									}
									priceDescription={
										product.currentVersion
											?.priceDescription ?? null
									}
									isOnlineApplication={Boolean(
										product.isOnlineApplication,
									)}
								/>
							);
						},
					)}
				</Grid>
				{familyName && (
					<NextLink href={`/${currentTopic}`} passHref>
						<Link
							id={`click_see_all_${currentTopic}_products`}
							variant="outline"
						>
							{t("productOverview.toFamilyPage", {
								familyName,
							})}
						</Link>
					</NextLink>
				)}
			</VStack>
		</chakra.div>
	);
};

// 🔬 TBD
